import { useEffect, useRef } from 'react';

import { expVal, expValNoExposure } from '@atlaskit/tmp-editor-statsig/expVal';
import { useAnalytics } from '@atlassian/conversation-assistant-instrumentation/controllers/analytics';
import type { AssistanceServiceInputParam } from '@atlassian/conversation-assistant-service/services/assistance-service/main';

import { useThirdPartySuggestedPromptEligibility } from './useThirdPartySuggestedPromptEligibility';

type ThirdPartyCwrPromptsCohort = 'control' | 'variant_a' | 'variant_b';

/**
 * The modal touchpoint — the only surface that fires the Statsig exposure. The
 * preloads warm the same store but must not "see" the experiment, so the exposure
 * fires only here, when the modal opens.
 */
const EXPOSURE_TOUCHPOINT = 'smart-create-prompt-suggestions';

export type UseThirdPartySuggestedPromptTileParams = AssistanceServiceInputParam & {
	/** True once the suggested-prompt tiles are shown in the modal (all cohorts). */
	tilesDisplayed: boolean;
};

export type ThirdPartySuggestedPromptTile = {
	/** Ask the backend to generate the 3P tile in this fetch. */
	enable3pPromptOn3rdCard: boolean;
	/**
	 * True once the 3P signal has settled (always true off a 3P surface). Callers must
	 * wait for this before fetching so the flag they send is stable.
	 */
	ready: boolean;
};

/**
 * Owns the 3P-suggested-prompt experiment for one touchpoint: it decides the fetch
 * flag from eligibility + cohort (no exposure, so preloads don't dilute the
 * experiment) and fires the Statsig exposure — plus an eligibility funnel event —
 * only when the tiles are shown in the modal.
 */
export const useThirdPartySuggestedPromptTile = ({
	assistanceServiceParams,
	tilesDisplayed,
}: UseThirdPartySuggestedPromptTileParams): ThirdPartySuggestedPromptTile => {
	const { touchpointSource } = assistanceServiceParams;
	const { sendAnalyticsEvent } = useAnalytics();

	const isExposureSurface = touchpointSource === EXPOSURE_TOUCHPOINT;

	// Cohort is a no-exposure read (in-memory, no network). Only variants A/B need the
	// connectors-derived flag, so only they wait on eligibility; control and everyone
	// else fetch suggestions immediately and still fire their exposure once connectors
	// settle in the background.
	const cohort = expValNoExposure(
		'confluence_cwr_3p_connection_suggested_prompts',
		'cohort',
		'control',
	) as ThirdPartyCwrPromptsCohort;
	const isVariant = cohort === 'variant_a' || cohort === 'variant_b';

	// Only load connectors where the 3P decision actually needs them: the exposure
	// surface (eligibility gates the exposure for every cohort, incl. control) or any
	// variant (which sends the fetch flag). Control on a preload surface does no
	// connectors work, so the create button doesn't fetch — or re-render — for it.
	const {
		connectorsLoaded,
		isEligible,
		adminAllowed3pAppCount,
		knowledgeSourceCount,
		connectorsErrored,
	} = useThirdPartySuggestedPromptEligibility({
		assistanceServiceParams,
		enabled: isExposureSurface || isVariant,
	});

	// Fire the Statsig exposure — and the eligibility funnel event — once, when the
	// tiles are actually shown in the open modal. Only eligible users fire the
	// exposure via `expVal`; the funnel event is sent for everyone so the "admin
	// allowed no 3P apps → not in the experiment" segment is still counted.
	const hasReportedExposureRef = useRef(false);
	useEffect(() => {
		// Wait for connectors to load before firing: eligibility (and therefore whether
		// this user fires an exposure at all) is unknown until then. Control does not gate
		// its fetch on connectors, so its tiles can display first — without this guard an
		// eligible control user would latch an `isEligible: false` result and never expose.
		if (
			!isExposureSurface ||
			!tilesDisplayed ||
			!connectorsLoaded ||
			hasReportedExposureRef.current
		) {
			return;
		}
		hasReportedExposureRef.current = true;

		const exposedCohort: ThirdPartyCwrPromptsCohort | null = isEligible
			? (expVal(
					'confluence_cwr_3p_connection_suggested_prompts',
					'cohort',
					'control',
				) as ThirdPartyCwrPromptsCohort)
			: null;

		sendAnalyticsEvent({
			eventType: 'track',
			action: 'evaluated',
			actionSubject: 'thirdPartySuggestedPromptEligibility',
			attributes: {
				eligibility: isEligible
					? 'eligible'
					: connectorsErrored
						? 'connectors_unavailable'
						: 'no_admin_allowed_3p_apps',
				cohort: exposedCohort,
				adminAllowed3pAppCount,
				knowledgeSourceCount,
				surface: touchpointSource,
			},
		});
	}, [
		isExposureSurface,
		tilesDisplayed,
		connectorsLoaded,
		isEligible,
		connectorsErrored,
		adminAllowed3pAppCount,
		knowledgeSourceCount,
		touchpointSource,
		sendAnalyticsEvent,
	]);

	return {
		enable3pPromptOn3rdCard: isVariant && isEligible,
		ready: isVariant ? connectorsLoaded : true,
	};
};
